import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Check, ZoomIn, X } from "lucide-react";
import ShopHeader from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  getProduct, SIZES, COLORS, FRAMING, FRAME_COLORS,
  priceFor, eur,
  LAUNCH_PRODUCTS,
  coverOf, COLLECTOR,
} from "@/lib/shop";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { useEditions } from "@/context/EditionsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SizeGuide({ t }) {
  // One SVG, one true scale: 1 cm = 1.15 px. Floor-aligned so proportions read honestly.
  // Prints are PORTRAIT (width × height): 40×50, 50×70, 70×100 cm. Sofa: 180 × 85 cm.
  const K = 1.15;
  const FLOOR = 165;
  const prints = [
    { label: "40 × 50 cm", w: 40 * K, h: 50 * K },
    { label: "50 × 70 cm", w: 50 * K, h: 70 * K },
    { label: "70 × 100 cm", w: 70 * K, h: 100 * K },
  ];
  const sofaW = 180 * K; // 207
  const sofaH = 85 * K; // ~98

  let x = 30;
  const printEls = prints.map((p) => {
    const el = (
      <g key={p.label}>
        <rect
          x={x}
          y={FLOOR - p.h}
          width={p.w}
          height={p.h}
          fill="#f1ede7"
          stroke="#111111"
          strokeOpacity="0.65"
          strokeWidth="1"
        />
        <text
          x={x + p.w / 2}
          y={FLOOR + 16}
          textAnchor="middle"
          fontSize="10"
          fill="#111111"
          fillOpacity="0.55"
          fontFamily="inherit"
        >
          {p.label}
        </text>
      </g>
    );
    x += p.w + 36;
    return el;
  });

  const sx = x + 20; // sofa x start
  const seatH = 42 * K;
  const armW = 16 * K;

  return (
    <div className="mt-12 border gallery-line bg-white px-6 py-8" data-testid="size-guide">
      <p className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">
        {t("product.sizeGuide")}
      </p>
      <svg
        viewBox="0 0 560 190"
        className="mt-6 w-full max-w-[560px]"
        role="img"
        aria-label={t("product.sizeGuide")}
      >
        {/* floor */}
        <line x1="10" y1={FLOOR} x2="550" y2={FLOOR} stroke="#111111" strokeOpacity="0.2" />
        {printEls}
        {/* sofa: back, seat, arms, legs */}
        <g stroke="#111111" strokeOpacity="0.5" fill="none" strokeWidth="1">
          <rect x={sx} y={FLOOR - sofaH} width={sofaW} height={sofaH - seatH * 0.4} rx="4" />
          <rect x={sx - armW} y={FLOOR - seatH - 14} width={armW} height={seatH + 14} rx="3" />
          <rect x={sx + sofaW} y={FLOOR - seatH - 14} width={armW} height={seatH + 14} rx="3" />
          <rect x={sx - armW} y={FLOOR - seatH} width={sofaW + armW * 2} height={seatH} rx="3" />
          <line x1={sx + 6} y1={FLOOR} x2={sx + 6} y2={FLOOR + 7} />
          <line x1={sx + sofaW - 6} y1={FLOOR} x2={sx + sofaW - 6} y2={FLOOR + 7} />
        </g>
        <text
          x={sx + sofaW / 2}
          y={FLOOR + 16}
          textAnchor="middle"
          fontSize="10"
          fill="#111111"
          fillOpacity="0.55"
          fontFamily="inherit"
        >
          180 cm
        </text>
      </svg>
      <p className="mt-5 text-xs font-light leading-relaxed text-[#111111]/50">
        {t("product.sizeNote")}
      </p>
    </div>
  );
}

export default function Product() {
  const { id } = useParams();
  const product = getProduct(id);
  const { addItem, openCart } = useCart();
  const { t, lang } = useLanguage();
  const { liveRemaining, liveEditionTotal, liveAlmostGone } = useEditions();
  const [color, setColor] = useState("noir");
  const [size, setSize] = useState("50x70");
  const [framing, setFraming] = useState("unframed");
  const [frameColor, setFrameColor] = useState("oak");
  const [tier, setTier] = useState(null); // null | "collector"
  const [added, setAdded] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notified, setNotified] = useState(false);

  // Hero = framed in-situ shot first; the flat print is secondary (Part 5).
  const hero = product?.gallery?.[0] || product?.prints?.noir;

  useEffect(() => {
    setColor("noir");
    setSize("50x70");
    setFraming("unframed");
    setFrameColor("oak");
    setTier(null);
    setNotified(false);
    setNotifyEmail("");
    if (product) setActiveImage(product.gallery?.[0] || product.prints.noir);
  }, [id, product]);

  const collectorOn = tier === "collector";
  const price = useMemo(
    () => (collectorOn ? COLLECTOR.price : priceFor(size, framing)),
    [size, framing, collectorOn]
  );
  const edition = liveEditionTotal(size, color, tier);
  const remaining = liveRemaining(id, size, color, tier);
  const soldOut = edition > 0 && remaining === 0;
  const almostGone = !soldOut && liveAlmostGone(id, size, color, tier);

  if (!product) {
    return (
      <div className="bg-[#faf8f5] min-h-screen">
        <ShopHeader solid />
        <div className="pt-40 text-center">
          <p className="font-light text-[#111111]/70">{t("product.notFound")}</p>
          <Link to="/shop" className="link-underline mt-4 inline-block">
            {t("product.backToShop")}
          </Link>
        </div>
      </div>
    );
  }

  const thumbs = [
    ...(product.gallery || []).map((g, i) => ({ src: g, label: `View ${i + 1}` })),
    { src: product.prints.noir, label: "Noir" },
    { src: product.prints.bicolor, label: "Bi-color" },
  ];

  const selectColor = (cid) => {
    setColor(cid);
    setActiveImage(framing === "framed" ? hero : product.prints[cid]);
  };
  const selectFraming = (fid) => {
    setFraming(fid);
    setActiveImage(fid === "framed" ? hero : product.prints[color]);
  };

  const onAdd = () => {
    addItem({
      product_id: product.id,
      product_name: product.name,
      color,
      size,
      framing: collectorOn ? "unframed" : framing,
      frame_color: !collectorOn && framing === "framed" ? frameColor : null,
      tier,
      quantity: 1,
      unit_price: price,
      image: framing === "framed" && !collectorOn ? hero : product.prints[color],
    });
    setAdded(true);
    toast.success(t("product.added"));
    setTimeout(() => setAdded(false), 2000);
  };

  const onNotify = async () => {
    try {
      await axios.post(`${API}/leads`, {
        email: notifyEmail,
        source: `notify-${product.id}-${size}-${color}`,
      });
      setNotified(true);
    } catch {
      toast.error(t("cart.error"));
    }
  };

  const crossSell = LAUNCH_PRODUCTS.filter((p) => p.id !== product.id).slice(0, 3);

  const addBlock = (
    <button
      onClick={onAdd}
      disabled={soldOut}
      data-testid="add-to-cart"
      className="flex w-full items-center justify-center gap-2 bg-[#111111] px-7 py-4 text-sm font-light tracking-wide text-[#faf8f5] transition-opacity duration-500 hover:opacity-85 disabled:opacity-40"
    >
      {added ? (
        <>
          <Check className="h-4 w-4" strokeWidth={1.5} /> {t("product.added")}
        </>
      ) : soldOut ? (
        t("product.soldOut")
      ) : (
        `${t("product.add")} — ${eur(price, lang)}`
      )}
    </button>
  );

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <ShopHeader solid />
      <main className="pt-20 md:pt-24 pb-32 md:pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 text-sm font-light text-[#111111]/70 hover:text-[#111111]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("product.back")}
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Visuals */}
            <div className="lg:col-span-7">
              <button
                type="button"
                onClick={() => setZoom(true)}
                data-testid="product-main-zoom"
                className="group relative block w-full border gallery-line bg-white overflow-hidden cursor-zoom-in"
              >
                <img
                  key={activeImage}
                  src={activeImage}
                  alt={`${product.name} — ${product.tagline}`}
                  data-testid="product-main-image"
                  className="aspect-[4/5] w-full object-cover"
                />
                <span className="absolute bottom-4 right-4 grid h-10 w-10 place-items-center rounded-full bg-[#111111]/80 text-[#faf8f5] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn className="h-4 w-4" strokeWidth={1.5} />
                </span>
              </button>
              <div className="mt-4 grid grid-cols-4 sm:grid-cols-5 gap-3">
                {thumbs.map((th, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`thumb-${i}`}
                    onClick={() => setActiveImage(th.src)}
                    aria-label={`${product.name} ${th.label}`}
                    className={`border bg-white overflow-hidden transition-colors duration-300 ${
                      activeImage === th.src
                        ? "border-[#111111]"
                        : "border-[#111111]/15 hover:border-[#111111]/50"
                    }`}
                  >
                    <img
                      src={th.src}
                      alt={`${product.name} ${th.label}`}
                      className="aspect-square w-full object-cover"
                    />
                  </button>
                ))}
              </div>
              <p className="mt-4 text-xs font-light text-[#111111]/45">
                {t("product.disclaimer")}
              </p>

              <SizeGuide t={t} />
            </div>

            {/* Options */}
            <div className="lg:col-span-5 lg:pl-4">
              <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
                {t("product.kicker")}
              </span>
              <h1 className="mt-4 font-display text-4xl md:text-5xl tracking-tight text-[#111111]">
                {product.name}
              </h1>
              <p className="mt-3 font-display italic text-xl text-[#a89f8e]">
                {product.tagline}
              </p>

              {/* Live edition counter */}
              {edition > 0 && (
                <p
                  className="mt-5 text-sm font-light text-[#111111]/70"
                  data-testid="edition-counter"
                >
                  {soldOut ? (
                    <span className="uppercase tracking-[0.2em] text-[#111111]">
                      {t("product.soldOut")}
                    </span>
                  ) : (
                    <>
                      <span className="font-display italic">{remaining}</span>{" "}
                      {t("product.remaining")} {edition} {t("product.remainingTail")}
                      {almostGone && (
                        <span
                          data-testid="almost-gone-badge"
                          className="ml-3 border border-[#111111]/30 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-[#111111]"
                        >
                          {t("product.almostGone")}
                        </span>
                      )}
                    </>
                  )}
                </p>
              )}

              <p className="mt-4 text-3xl font-light text-[#111111]" data-testid="product-price">
                {eur(price, lang)}
              </p>
              <p className="mt-3 text-xs font-light leading-relaxed text-[#111111]/55" data-testid="fulfil-line">
                {t("fulfil.line")}
              </p>
              {framing === "framed" && size === "70x100" && (
                <p className="mt-2 text-xs font-light leading-relaxed text-[#111111]/55">
                  {t("fulfil.specialist")}
                </p>
              )}

              {/* Concept copy — the absence, the room. Never the artist's style. */}
              <p className="mt-6 text-sm font-light leading-relaxed text-[#111111]/65">
                {t("product.concept")}
              </p>

              {/* Colourway */}
              <div className="mt-8">
                <p className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">
                  {t("product.color")}
                </p>
                <div className="mt-3 flex gap-3">
                  {COLORS.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`color-${c.id}`}
                      onClick={() => selectColor(c.id)}
                      className={`rounded-full border px-5 py-2 text-sm font-light transition-colors duration-300 ${
                        color === c.id
                          ? "border-[#111111] bg-[#111111] text-[#faf8f5]"
                          : "border-[#111111]/25 text-[#111111]/80 hover:border-[#111111]"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div className="mt-7">
                <p className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">
                  {t("product.size")}
                </p>
                <div className="mt-3 flex flex-col gap-3">
                  {SIZES.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`size-${s.id}`}
                      onClick={() => setSize(s.id)}
                      className={`flex items-center justify-between border px-5 py-3 text-left transition-colors duration-300 ${
                        size === s.id
                          ? "border-[#111111] bg-[#f1ede7]"
                          : "border-[#111111]/20 hover:border-[#111111]/50"
                      }`}
                    >
                      <span className="font-light text-[#111111]">{s.label}</span>
                      <span className="text-sm text-[#111111]/70">
                        {eur(s.prices[framing], lang)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Framing */}
              <div className="mt-7">
                <p className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">
                  {t("product.framing")}
                </p>
                <div className="mt-3 flex gap-3">
                  {FRAMING.map((f) => (
                    <button
                      key={f.id}
                      data-testid={`framing-${f.id}`}
                      onClick={() => selectFraming(f.id)}
                      className={`flex-1 border px-5 py-3 text-sm font-light transition-colors duration-300 ${
                        framing === f.id
                          ? "border-[#111111] bg-[#f1ede7] text-[#111111]"
                          : "border-[#111111]/20 text-[#111111]/80 hover:border-[#111111]/50"
                      }`}
                    >
                      {t(`product.${f.id}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Frame colour — only when framed */}
              {framing === "framed" && (
                <div className="mt-7">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#111111]/50">
                    {t("product.frameColor")}
                  </p>
                  <div className="mt-3 flex gap-3">
                    {FRAME_COLORS.map((f) => (
                      <button
                        key={f.id}
                        data-testid={`frame-${f.id}`}
                        onClick={() => setFrameColor(f.id)}
                        className={`flex-1 border px-5 py-3 text-sm font-light transition-colors duration-300 ${
                          frameColor === f.id
                            ? "border-[#111111] bg-[#f1ede7] text-[#111111]"
                            : "border-[#111111]/20 text-[#111111]/80 hover:border-[#111111]/50"
                        }`}
                      >
                        {t(f.id === "oak" ? "product.oak" : "product.blackOak")}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Collector edition — 70×100 unframed only */}
              {size === "70x100" && framing === "unframed" && (
                <div className="mt-7" data-testid="collector-block">
                  <button
                    onClick={() => setTier(collectorOn ? null : "collector")}
                    data-testid="collector-toggle"
                    className={`flex w-full items-center justify-between border px-5 py-4 text-left transition-colors duration-300 ${
                      collectorOn ? "border-[#111111] bg-[#111111] text-[#faf8f5]" : "border-[#111111]/30 hover:border-[#111111]"
                    }`}
                  >
                    <span className="text-sm font-light">{t("collector.label")}</span>
                    <span className="text-sm">{eur(COLLECTOR.price, lang)}</span>
                  </button>
                  {collectorOn && (
                    <p className="mt-2 text-xs font-light leading-relaxed text-[#111111]/55">
                      {t("collector.note")}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-9 hidden md:block">{addBlock}</div>

              {soldOut && !notified && (
                <div className="mt-4 flex gap-2" data-testid="notify-form">
                  <input
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder={t("product.notifyPlaceholder")}
                    data-testid="notify-email"
                    className="flex-1 border border-[#111111]/25 bg-white px-4 py-3 text-sm font-light outline-none focus:border-[#111111]"
                  />
                  <button
                    onClick={onNotify}
                    data-testid="notify-submit"
                    className="border border-[#111111] px-5 py-3 text-sm font-light hover:bg-[#111111] hover:text-[#faf8f5] transition-colors duration-300"
                  >
                    {t("product.notify")}
                  </button>
                </div>
              )}
              {soldOut && notified && (
                <p className="mt-4 text-sm font-light text-[#111111]/70" data-testid="notify-thanks">
                  {t("product.notifyThanks")}
                </p>
              )}

              {/* Detail blocks */}
              <Accordion type="single" collapsible className="mt-10 border-t border-[#111111]/12">
                {["paper", "framing", "edition", "shipping"].map((k) => (
                  <AccordionItem key={k} value={k} className="border-[#111111]/12">
                    <AccordionTrigger
                      data-testid={`detail-${k}`}
                      className="text-xs uppercase tracking-[0.22em] text-[#111111]/70 font-light hover:no-underline"
                    >
                      {t(`product.d.${k}.t`)}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm font-light leading-relaxed text-[#111111]/65">
                      {t(`product.d.${k}.b`)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* Complete the wall */}
          {crossSell.length > 0 && (
            <Reveal>
              <section className="mt-24 md:mt-32" data-testid="complete-the-wall">
                <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
                  {t("product.completeWall")}
                </span>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 border-t border-l gallery-line">
                  {crossSell.map((p) => (
                    <Link
                      key={p.id}
                      to={`/shop/${p.id}`}
                      data-testid={`crosssell-${p.id}`}
                      className="group border-r border-b gallery-line"
                    >
                      <div className="overflow-hidden bg-white">
                        <img
                          src={coverOf(p)}
                          alt={p.tagline}
                          loading="lazy"
                          className="aspect-[4/5] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]"
                        />
                      </div>
                      <div className="flex items-end justify-between gap-4 px-6 py-5">
                        <h3 className="font-display italic text-lg tracking-tight text-[#111111]">
                          {p.name}
                        </h3>
                        <span className="text-sm font-light text-[#111111]/70">
                          {t("shop.from")} {eur(SIZES[0].prices.unframed, lang)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            </Reveal>
          )}
        </div>
      </main>

      {/* Sticky add-to-cart bar — mobile */}
      {!soldOut && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-[#111111]/12 bg-[#faf8f5]/95 backdrop-blur-md px-5 py-3 flex items-center gap-4"
          data-testid="sticky-atc"
        >
          <span className="font-display text-lg text-[#111111]">{eur(price, lang)}</span>
          <div className="flex-1">{addBlock}</div>
        </div>
      )}

      {zoom && (
        <div
          data-testid="zoom-overlay"
          onClick={() => setZoom(false)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[#111111]/92 p-6 cursor-zoom-out"
        >
          <button
            type="button"
            data-testid="zoom-close"
            onClick={() => setZoom(false)}
            className="absolute top-6 right-6 grid h-11 w-11 place-items-center rounded-full border border-[#faf8f5]/40 text-[#faf8f5] hover:bg-[#faf8f5] hover:text-[#111111] transition-colors duration-300"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <img
            src={activeImage}
            alt={`${product.name} — zoom`}
            data-testid="zoom-image"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[92vw] object-contain"
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
